import type { FragmentType } from "../../../../__generated__/gql";
import { graphql, useFragment } from "../../../../__generated__/gql";
import {
  DirectionsBikeIcon,
  DirectionsBoatIcon,
  DirectionsBusIcon,
  DirectionsCarIcon,
  DirectionsWalkIcon,
  FlightIcon,
  MapIcon,
  RouteIcon,
  TrainIcon,
} from "../../../_components/dx-icons";

import { DxCollapsibleSection } from "./dx-collapsible-section";
import { DxDivider } from "./dx-divider";
import { DxIconButton } from "./dx-icon-button";

// タクシー・代行は拠点別ではなく全社統一のヘルプ記事に飛ばす方針。
const TAXI_HELP_URL =
  "https://help.sa-nu.com/hc/ja/articles/59532263409561-%E6%8B%A0%E7%82%B9%E5%91%A8%E8%BE%BA%E3%81%A7%E5%88%A9%E7%94%A8%E3%81%A7%E3%81%8D%E3%82%8B%E3%82%BF%E3%82%AF%E3%82%B7%E3%83%BC%E3%82%84%E9%81%8B%E8%BB%A2%E4%BB%A3%E8%A1%8C%E3%82%92%E7%9F%A5%E3%82%8A%E3%81%9F%E3%81%84";

type IconComponent = (props: { className?: string }) => React.ReactNode;

export const DxAccessSectionFragment = graphql(`
  fragment DxAccessSection_Site on Site {
    name
    content {
      id
      postalCode
      prefecture
      city
      addressLine
      googleMapsUrl
      accessRouteGroups {
        id
        title
        transportIcon
        sort
        points {
          id
          name
          legToNextDescription
          sort
        }
      }
    }
  }
`);

type AccessPoint = {
  id: string;
  name: string;
  legToNextDescription?: string | null;
  sort: number;
};

type AccessGroup = {
  id: string;
  title: string;
  transportIcon: string;
  points: AccessPoint[];
};

// Admin の access CRUD と同じ transportIcon 全種を map する。 未対応値と Other は RouteIcon。
const TRANSPORT_ICONS: Record<string, IconComponent> = {
  Train: TrainIcon,
  Car: DirectionsCarIcon,
  Bus: DirectionsBusIcon,
  Plane: FlightIcon,
  Ferry: DirectionsBoatIcon,
  Walk: DirectionsWalkIcon,
  Bicycle: DirectionsBikeIcon,
};

function AccessRoute({
  group,
  destinationLabel,
  postalLine,
  localityLine,
}: {
  group: AccessGroup;
  destinationLabel: string;
  postalLine: string | null;
  localityLine: string | null;
}) {
  const Icon = TRANSPORT_ICONS[group.transportIcon] ?? RouteIcon;
  const points = [...group.points].sort((a, b) => a.sort - b.sort);

  return (
    // Figma AccessItem: 各列は pt-0 / pb-16 (px-16 は親 grid が担う)。 見出し⇔経路は
    // Figma の Slot pt-4 に合わせ 4px。 先頭のアイコン/ドット/連結線はすべて 20px 幅の枠に
    // 収め gap-12 で、 中心 (左端から 10px) と テキスト左端 (20+12=32px) を Figma と揃える。
    <div className="pb-[16px]">
      <div className="flex items-center gap-[12px] pb-[4px]">
        <Icon className="text-dx-text-default size-[20px] shrink-0" />
        <h3 className="dx-subhead text-dx-text-default">{group.title}</h3>
      </div>
      {/* 経路/区間/ゴールの全要素を min-h 24px・上下 padding 0 に統一
          (Figma 3053-18080: 1 行に収まる要素は一律 24px。 dx-body-2-bold は lh-24)。 */}
      <ol>
        {points.map((point) => (
          <li key={point.id}>
            <div className="flex min-h-[24px] items-center gap-[12px]">
              {/* ドットは 20px 枠の中央に置き、 見出しアイコン (20px) と中心を揃える。 */}
              <span className="flex size-[20px] flex-none items-center justify-center">
                <span className="bg-dx-text-tertiary size-[10px] rounded-full" />
              </span>
              <span className="dx-body-2-bold text-dx-text-default">
                {point.name}
              </span>
            </div>
            {point.legToNextDescription && (
              // 連結線もドット中心 (左端から 10px) に合わせる: ml-9 + border-l-2 で
              // 線を 9〜11px に置き、 pl-21 でテキストを 32px に揃える。
              <div className="border-dx-text-tertiary ml-[9px] flex min-h-[24px] items-center border-l-2 pl-[21px]">
                <span className="dx-body-3 text-dx-text-default">
                  {point.legToNextDescription}
                </span>
              </div>
            )}
          </li>
        ))}
        <li className="flex min-h-[24px] items-center gap-[12px]">
          <span className="flex size-[20px] flex-none items-center justify-center">
            <span className="bg-dx-button-dark-fill size-[10px] rounded-full" />
          </span>
          <span className="bg-dx-button-dark-fill text-dx-base-white dx-body-2-bold rounded-full px-[12px]">
            {destinationLabel}
          </span>
        </li>
      </ol>
      {(postalLine || localityLine) && (
        <div className="dx-body-3 text-dx-text-sub pt-[8px] pl-[32px]">
          {postalLine && <div>{postalLine}</div>}
          {localityLine && <div>{localityLine}</div>}
        </div>
      )}
    </div>
  );
}

export function DxAccessSection({
  site: siteData,
  defaultOpen = false,
}: {
  site: FragmentType<typeof DxAccessSectionFragment>;
  defaultOpen?: boolean;
}) {
  const site = useFragment(DxAccessSectionFragment, siteData);
  const { content } = site;
  const groups = [...content.accessRouteGroups].sort((a, b) => a.sort - b.sort);
  const destinationLabel = site.name;
  const googleMapsUrl = content.googleMapsUrl;
  const postalLine = content.postalCode ? `〒${content.postalCode}` : null;
  const localityLine =
    [content.prefecture, content.city, content.addressLine]
      .filter((part): part is string => Boolean(part))
      .join("") || null;
  return (
    <>
      <DxDivider />
      <DxCollapsibleSection
        title="アクセス"
        defaultOpen={defaultOpen}
        trailing={
          <>
            {googleMapsUrl && (
              <DxIconButton
                variant="transparent"
                size="small"
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                ariaLabel="Google マップで開く"
                data-analytics-id="site_access_clicked_google_maps"
                icon={<MapIcon />}
              />
            )}
            {/* タクシー・代行はテキストリンクでなくアイコンボタンで配置 (DXPJ-62 XowDks)。 */}
            <DxIconButton
              variant="transparent"
              size="small"
              href={TAXI_HELP_URL}
              target="_blank"
              rel="noopener noreferrer"
              ariaLabel="タクシー・代行のご案内"
              data-analytics-id="site_access_clicked_taxi_guide"
              icon={<DirectionsCarIcon />}
            />
          </>
        }
      >
        {groups.length > 0 ? (
          <div className="grid grid-cols-1 gap-[32px] px-[16px] lg:grid-cols-2">
            {groups.map((group) => (
              <AccessRoute
                key={group.id}
                group={group}
                destinationLabel={destinationLabel}
                postalLine={postalLine}
                localityLine={localityLine}
              />
            ))}
          </div>
        ) : (
          /* 経路情報が無い拠点でも住所は基本情報として 1 回だけ出す
           * (Figma に経路 0 件のデザインは無いため fallback)。 */
          (postalLine || localityLine) && (
            <div className="dx-body-3 text-dx-text-sub px-[16px]">
              {postalLine && <div>{postalLine}</div>}
              {localityLine && <div>{localityLine}</div>}
            </div>
          )
        )}
      </DxCollapsibleSection>
    </>
  );
}
